import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Activity,
  ChevronDown,
  ChevronUp,
  Globe,
  Headphones,
  Mic,
  Phone,
  TriangleAlert,
  Volume2,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { SELECTABLE_EDGES, edgeLabel, parseEdge } from './twilio-edge';
import { REBUILD_CAP_ALERT } from './device-rebuild';
import { PqFixDialog } from './PqFixDialog';
import type { TwilioPhone, TwilioStatus, CallQuality } from './useTwilioPhone';
import type { BffHealth } from '../useBffPing';
import type { ConnQuality } from '../bff';

// =====================================================================
// TwilioControlBox — the always-on, top-left softphone panel. Alive for the
// whole logged-in session (independent of pool/phase). Surfaces, off Twilio's
// OWN single mic stream + the BFF ping link (no second microphone):
//   - audio-socket status (+ hover: device RTT / warnings / errors)
//   - BFF connection quality (+ hover: RTT / jitter / misses / reconnects)
//   - a mic-activity wave + a remote-speaking wave
//   - a speaker-volume slider floored at 20%, and a test-tone button
// Twilio metering needs a live registered Device; until the core mints a Voice
// token there's no device, so the waves/volume/test render DISABLED and
// only the BFF-health half is live.
// =====================================================================

const COLLAPSE_KEY = 'maestro.twilioBox.collapsed';

const TWILIO_DOT: Record<TwilioStatus, string> = {
  idle: 'bg-muted-foreground',
  connecting: 'bg-yellow-500',
  ready: 'bg-emerald-500',
  on_call: 'bg-blue-500',
  error: 'bg-red-500',
};

const QUALITY_DOT: Record<ConnQuality, string> = {
  excellent: 'bg-emerald-500',
  good: 'bg-emerald-400',
  degraded: 'bg-amber-500',
  offline: 'bg-red-500',
};

const QUALITY_LABEL: Record<ConnQuality, string> = {
  excellent: 'excellent',
  good: 'good',
  degraded: 'degraded',
  offline: 'offline',
};

const CALL_QUALITY_LABEL: Record<CallQuality, string> = {
  excellent: 'excellent',
  good: 'good',
  degraded: 'degraded',
  unknown: 'no call',
};

function Dot({ className }: { className: string }) {
  return <span aria-hidden className={cn('inline-block size-2.5 rounded-full', className)} />;
}

// A scrolling bar waveform fed from a level accessor sampled in rAF (never React
// state — the underlying volume events fire at ~60Hz). When `active` is false the rAF
// stops and the bars sit at a greyed, flat baseline; what makes a meter inactive is the
// caller's to decide. Exported for the manual dialer's softphone box, which meters its
// own Device the same way.
export function WaveMeter({
  getLevel,
  tone,
  active,
  bars = 16,
}: {
  getLevel: () => number;
  tone: 'input' | 'output';
  active: boolean;
  bars?: number;
}) {
  const barRefs = useRef<Array<HTMLDivElement | null>>([]);
  const history = useRef<number[]>(new Array(bars).fill(0));

  useEffect(() => {
    const setAll = (v: number) => {
      for (let i = 0; i < bars; i++) {
        const el = barRefs.current[i];
        if (el) el.style.transform = `scaleY(${v})`;
      }
    };
    if (!active) {
      history.current = new Array(bars).fill(0);
      setAll(0.08); // flat baseline while inactive
      return;
    }
    let raf = 0;
    const loop = () => {
      const lvl = Math.min(1, Math.max(0, getLevel()) * 1.8); // amplify quiet speech
      const h = history.current;
      h.push(lvl);
      h.shift();
      for (let i = 0; i < bars; i++) {
        const el = barRefs.current[i];
        if (el) el.style.transform = `scaleY(${0.08 + (h[i] ?? 0) * 0.92})`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [getLevel, bars, active]);

  return (
    <div className={cn('flex h-7 items-center gap-px', !active && 'opacity-40')} aria-hidden>
      {Array.from({ length: bars }).map((_, i) => (
        <div
          key={i}
          ref={(el) => {
            barRefs.current[i] = el;
          }}
          className={cn(
            'h-full flex-1 origin-center rounded-full',
            tone === 'input' ? 'bg-emerald-500/80' : 'bg-blue-500/80',
          )}
          style={{ transform: 'scaleY(0.08)' }}
        />
      ))}
    </div>
  );
}

function AudioSocketStatus({ twilio }: { twilio: TwilioPhone }) {
  const connected = twilio.status === 'ready' || twilio.status === 'on_call';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground flex items-center gap-1.5 text-xs"
          data-testid="twilio-socket"
        >
          <Dot className={TWILIO_DOT[twilio.status]} />
          <span>Audio {twilio.status}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start">
        <div className="space-y-0.5">
          <div>
            Twilio Voice device: <span className="font-medium">{twilio.status}</span>
          </div>
          {!connected ? (
            <div>No Voice token yet — controls disabled until the device connects.</div>
          ) : null}
          <div>Round-trip: {twilio.deviceRtt == null ? '—' : `${twilio.deviceRtt}ms`}</div>
          <div>
            Call quality: <span className="font-medium">{CALL_QUALITY_LABEL[twilio.quality]}</span>
          </div>
          {twilio.warnings.length > 0 ? (
            <div className="text-amber-500">warnings: {twilio.warnings.join(', ')}</div>
          ) : null}
          {twilio.lastError ? (
            <div className="text-red-500">last error: {twilio.lastError}</div>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function ConnQualityStatus({ health }: { health: BffHealth }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground flex items-center gap-1.5 text-xs"
          data-testid="bff-quality"
        >
          <Dot className={QUALITY_DOT[health.quality]} />
          <span>Link {QUALITY_LABEL[health.quality]}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end">
        <div className="space-y-0.5">
          <div>
            BFF WebSocket: <span className="font-medium">{health.status}</span>
            {health.online ? '' : ' · browser offline'}
          </div>
          <div>RTT: {health.rtt == null ? '—' : `${health.rtt}ms`}</div>
          <div>Jitter: {`${Math.round(health.jitterMs)}ms`}</div>
          <div>
            Dropped pings: {health.missCount} · reconnects: {health.reconnectCount}
          </div>
          <div>
            Last pong:{' '}
            {health.lastPongAgeMs == null
              ? '—'
              : `${Math.round(health.lastPongAgeMs / 100) / 10}s ago`}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function OutputVolumeSlider({
  value,
  onChange,
  disabled,
}: {
  value: number; // 0.2–1.0
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const pct = Math.round(value * 100);
  return (
    <label className={cn('flex items-center gap-2 text-xs', disabled && 'opacity-50')}>
      <Volume2 className="text-muted-foreground size-4 shrink-0" />
      <input
        type="range"
        min={20}
        max={100}
        step={5}
        value={pct}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
        className={cn(
          'accent-primary h-1 w-full',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
        aria-label="Speaker volume"
        data-testid="twilio-volume"
      />
      <span className="text-muted-foreground w-9 text-right tabular-nums">{pct}%</span>
    </label>
  );
}

// A labelled <select> over the available mic / speaker devices. Switching applies
// to the live call without dropping it (Twilio re-acquires / re-routes).
//
// `nudge` pulses the icon amber and explains why on hover. It is a prompt, not an
// error: the SDR is audible either way, just worse than they could be — so it
// deliberately doesn't get the red banner treatment `deviceAlert` gets.
// Exported for the manual dialer's softphone box — same picker, its own preferences.
export function DeviceSelect({
  label,
  icon: Icon,
  value,
  devices,
  onChange,
  disabled,
  nudge,
}: {
  label: string;
  icon: typeof Mic;
  value: string | null;
  devices: MediaDeviceInfo[];
  onChange: (deviceId: string) => void;
  disabled?: boolean;
  nudge?: string | null;
}) {
  const icon = (
    <Icon
      className={cn(
        'size-4 shrink-0',
        nudge
          ? 'animate-mic-nudge motion-reduce:animate-none text-amber-500'
          : 'text-muted-foreground',
      )}
    />
  );
  return (
    <label className={cn('flex items-center gap-2 text-xs', disabled && 'opacity-50')}>
      {nudge ? (
        <Tooltip>
          {/* Focusable so the explanation is reachable without a pointer — the icon
              is the only thing carrying the signal. */}
          <TooltipTrigger asChild>
            <span tabIndex={0} data-testid="twilio-mic-nudge" aria-label={nudge}>
              {icon}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start">
            {nudge}
          </TooltipContent>
        </Tooltip>
      ) : (
        icon
      )}
      <select
        value={value ?? ''}
        disabled={disabled || devices.length === 0}
        onChange={(e) => onChange(e.target.value)}
        className="border-input bg-background h-7 w-full min-w-0 rounded-md border px-1 text-xs"
        aria-label={`${label} device`}
        data-testid={`twilio-device-${label.toLowerCase()}`}
      >
        {devices.length === 0 ? <option value="">—</option> : null}
        {devices.map((d) => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.label || d.deviceId || label}
          </option>
        ))}
      </select>
    </label>
  );
}

// Which Twilio edge this browser connects through. Exists because the state is
// otherwise invisible: an SDR pinned by a link has no way to know, and no way out.
//
// Unlike the mic and speaker pickers above, a change here cannot apply to the live
// session — see `edgePending` on the hook — so it is shown as pending rather than
// quietly deferred.
// Only for a session that HAS a region — pinned, or given one by the regional
// default. Shown to anyone else it is a datacentre name they never chose and cannot
// act on, and noise next to a dropdown is an invitation.
function edgeCaption(twilio: TwilioPhone, onCall: boolean): string | null {
  if (twilio.edgePending) {
    return onCall
      ? 'Not applied yet — reload when you are between calls'
      : 'Not applied yet — reload to use this region';
  }
  const active = twilio.activeEdge;
  if (twilio.edgeSource === 'geo') {
    // Said before the connection exists too: `activeEdge` only arrives once the Device
    // registers, and until then the row names a country nobody chose. Once it does
    // arrive it can disagree — the chain falls back to roaming — and a fallback must
    // not read as a region successfully set.
    if (!active) return 'Set automatically for your region';
    return active === twilio.requestedEdge
      ? `Set for your region — connected via ${edgeLabel(active)}`
      : `Set for your region, but connected via ${edgeLabel(active)}`;
  }
  return twilio.requestedEdge && active ? `Connected via ${edgeLabel(active)}` : null;
}

function EdgeSelect({ twilio }: { twilio: TwilioPhone }) {
  const onCall = twilio.status === 'on_call';
  const captionId = 'twilio-edge-caption';
  const caption = edgeCaption(twilio, onCall);
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 text-xs">
        <Globe className="text-muted-foreground size-4 shrink-0" />
        <select
          value={twilio.requestedEdge ?? ''}
          // Through the allowlist, never a cast: this value reaches the Twilio SDK,
          // and `parseEdge` already maps '' to null.
          onChange={(e) => twilio.selectEdge(parseEdge(e.target.value))}
          className="border-input bg-background h-7 w-full min-w-0 rounded-md border px-1 text-xs"
          aria-label="Connection region"
          aria-describedby={caption ? captionId : undefined}
          data-testid="twilio-edge"
        >
          {/* Where a regional default exists, "recommended" is a claim we measured false. */}
          <option value="">{twilio.geoEdge ? 'Automatic' : 'Automatic (recommended)'}</option>
          {SELECTABLE_EDGES.map((edge) => (
            <option key={edge} value={edge}>
              {edgeLabel(edge)}
            </option>
          ))}
        </select>
      </label>
      {caption ? (
        // role=status so the transition to "not applied yet" is announced; without it
        // a screen-reader user changes the region and hears nothing at all.
        <div
          id={captionId}
          role="status"
          className={cn(
            'pl-6 text-[11px]',
            twilio.edgePending ? 'text-amber-600' : 'text-muted-foreground',
          )}
        >
          {caption}
        </div>
      ) : null}
      {twilio.edgePending ? (
        <div className="pl-6">
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[11px]"
            // Reloading destroys the Device, which drops whatever call is running.
            disabled={onCall}
            title={onCall ? 'Finish your call first — reloading would end it.' : undefined}
            onClick={() => window.location.reload()}
            data-testid="twilio-edge-reload"
          >
            Reload
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// Chrome's post-quantum key agreement inflates the DTLS ClientHello past MTU,
// costing roughly a second at the start of every call. We cannot turn it off for
// them — no WebRTC API exposes it, and a page cannot touch chrome://flags — so this
// row detects the condition from their own call telemetry and offers the steps.
//
// The button is ALWAYS available to a Chrome SDR — the measurement only chooses the
// caption. Gating visibility on the verdict would make the instructions reachable
// only for people our (still provisional) threshold classifies correctly, and if it
// is wrong the row vanishes for exactly those who need it, undetectably.
//
// Two captions, and NEITHER claims success. There was a "✅ engaged" state; it told
// SDRs their problem was solved while their calls ran as slow as everyone else's.
// Until a same-browser before/after settles what a fixed handshake actually looks
// like, this row says only what it can support: either we have measured you as slow,
// or we have not.
function PqFixRow({ twilio }: { twilio: TwilioPhone }) {
  const [open, setOpen] = useState(false);
  const onCall = twilio.status === 'on_call';

  if (twilio.pqUi === 'hidden') return null;

  const measuredSlow = twilio.pqUi === 'prompt';

  return (
    <div className="space-y-1">
      <Button
        size="sm"
        variant="outline"
        className="h-7 w-full justify-start px-2 text-[11px]"
        onClick={() => {
          twilio.reportPqOpened();
          setOpen(true);
        }}
        data-testid="pq-fix-open"
      >
        <Zap
          className={cn(
            'size-3.5',
            // Amber and moving only when we have actually measured them as slow. An
            // unmeasured SDR gets the same button resting — present, but not
            // competing for attention.
            measuredSlow ? 'text-amber-500' : 'text-muted-foreground',
            // The box is on screen during live calls. Something animating for
            // attention while an SDR is talking to a prospect is a real cost, and
            // this is never urgent enough to pay it.
            measuredSlow && !onCall && 'animate-mic-nudge motion-reduce:animate-none',
          )}
        />
        Magic go-faster button
      </Button>
      {/* role=status so a screen-reader user hears the caption change when their
          calls are first measured as slow. */}
      <div
        role="status"
        className="text-muted-foreground pl-6 text-[11px]"
        data-testid="pq-fix-caption"
      >
        {measuredSlow
          ? 'Your calls are connecting slowly'
          : // Claims nothing either way — we cannot yet tell a fixed browser from a
            // lucky network path.
            'Calls slow to connect? Try this'}
      </div>
      <PqFixDialog
        open={open}
        onClose={() => setOpen(false)}
        onApplied={() => {
          twilio.markPqApplied();
          setOpen(false);
        }}
      />
    </div>
  );
}

export function TwilioControlBox({
  twilio,
  bffHealth,
  seatedForCalls = false,
}: {
  twilio: TwilioPhone;
  // True whenever a predictive call could arrive — in a pool, or joining one. NOT the
  // same as being on a call: a seated, waiting SDR shows `status: 'ready'`, and the
  // inbound handler auto-accepts. Gates the connection test, which would otherwise be
  // competing with a real lead for the same browser and microphone.
  seatedForCalls?: boolean;
  bffHealth: BffHealth;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // ignore (private mode)
      }
      return next;
    });
  }, []);

  // Controls are live only against a real, registered Device; the remote wave
  // needs an active call. Otherwise everything renders disabled.
  const connected = twilio.status === 'ready' || twilio.status === 'on_call';
  const onCall = twilio.status === 'on_call';

  // Only nudge while the device is live — with no registered Device the picker is
  // disabled, so pointing at a control they cannot use is just noise.
  const micNudge =
    connected && twilio.idleExternalMic
      ? `You're on the built-in microphone with "${twilio.idleExternalMic}" connected. Prospects hear you better on the headset — switch below.`
      : null;

  return (
    <TooltipProvider>
      <Card
        className="fixed top-4 left-4 z-40 w-[min(18rem,calc(100vw-2rem))] gap-3 py-3"
        data-testid="twilio-control-box"
      >
        <div className="flex items-center justify-between px-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Phone className="size-4" />
            Twilio
            {/* The mic picker lives in the collapsed-away body, so when the panel is
                shut the nudge has nowhere to render. Repeat it in the header, where
                clicking through to the picker is one toggle away. */}
            {collapsed && micNudge ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} data-testid="twilio-mic-nudge-collapsed" aria-label={micNudge}>
                    <Mic className="animate-mic-nudge motion-reduce:animate-none size-4 text-amber-500" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  {micNudge}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="text-muted-foreground hover:text-foreground"
            aria-label={collapsed ? 'Expand Twilio controls' : 'Collapse Twilio controls'}
            data-testid="twilio-collapse"
          >
            {collapsed ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
          </button>
        </div>

        {/* Status dots stay visible even when collapsed — the at-a-glance health. */}
        <div className="flex items-center justify-between gap-2 px-3">
          <AudioSocketStatus twilio={twilio} />
          <ConnQualityStatus health={bffHealth} />
        </div>

        {/* Outside the collapsed-away body on purpose: the collapse state is
            localStorage-persisted, and these can be the ONLY recovery
            instruction the SDR gets (e.g. auto-rebuild gave up and their seat
            is dead until they refresh). A critical alert nobody can see is a
            silent zombie with extra steps. Two independent banners, not one
            shared slot: "recovery gave up" and "your headset vanished" can
            both be true at once, and each has a different fix. */}
        {twilio.rebuildCapped ? (
          <div className="px-3">
            <div
              className="flex items-start gap-1.5 rounded-md bg-red-500/10 px-2 py-1.5 text-[11px] text-red-600"
              data-testid="twilio-rebuild-capped"
            >
              <TriangleAlert className="mt-px size-3.5 shrink-0" />
              <span>{REBUILD_CAP_ALERT}</span>
            </div>
          </div>
        ) : null}
        {twilio.deviceAlert ? (
          <div className="px-3">
            <div
              className="flex items-start gap-1.5 rounded-md bg-red-500/10 px-2 py-1.5 text-[11px] text-red-600"
              data-testid="twilio-device-alert"
            >
              <TriangleAlert className="mt-px size-3.5 shrink-0" />
              <span>{twilio.deviceAlert}</span>
            </div>
          </div>
        ) : null}

        {!collapsed ? (
          <div className="space-y-3 px-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <div className="text-muted-foreground text-[10px] tracking-wide uppercase">Mic</div>
                <WaveMeter getLevel={twilio.getInputLevel} tone="input" active={connected} />
              </div>
              <div className="space-y-1">
                <div className="text-muted-foreground text-[10px] tracking-wide uppercase">
                  Caller
                </div>
                <WaveMeter getLevel={twilio.getOutputLevel} tone="output" active={onCall} />
              </div>
            </div>

            <OutputVolumeSlider
              value={twilio.outputVolume}
              onChange={twilio.setOutputVolume}
              disabled={!connected}
            />

            <div className="space-y-2">
              <DeviceSelect
                label="Mic"
                icon={Mic}
                value={twilio.currentInputId}
                devices={twilio.inputDevices}
                onChange={twilio.selectInputDevice}
                disabled={!connected}
                nudge={micNudge}
              />
              {twilio.outputSelectionSupported ? (
                <DeviceSelect
                  label="Speaker"
                  icon={Headphones}
                  value={twilio.currentOutputId}
                  devices={twilio.outputDevices}
                  onChange={twilio.selectOutputDevice}
                  disabled={!connected}
                />
              ) : (
                <div className="text-muted-foreground flex items-center gap-2 text-[11px]">
                  <Headphones className="size-4 shrink-0" />
                  Speaker: browser default
                </div>
              )}
              <EdgeSelect twilio={twilio} />
              <PqFixRow twilio={twilio} />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={twilio.playTestTone}
              disabled={!connected}
              className="w-full"
              data-testid="twilio-test"
              title="Play a test tone"
            >
              <Volume2 />
              Test
            </Button>
            {/* Twilio preflight. Manual and explicit: it places a real (short, billable)
                test call to Twilio's echo service — no lead is dialled — and reports the
                signalling / ICE / DTLS breakdown plus whether media is relayed. That is
                the diagnostic a live call cannot give, and it means an SDR can help
                without fielding a real call.
                Disabled mid-call: a second connection would compete with the one they
                are on, and the numbers would be about that contention rather than
                their network. */}
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={twilio.runConnectionTest}
                disabled={
                  !connected || seatedForCalls || twilio.connectionTest.status === 'running'
                }
                className="w-full"
                data-testid="twilio-preflight"
                title={
                  seatedForCalls
                    ? 'Leave the pool first — a lead could connect while the test is running'
                    : 'Runs a ~15 second test call to Twilio (not to a lead) and sends the results to the team'
                }
              >
                <Activity />
                {twilio.connectionTest.status === 'running'
                  ? 'Testing connection…'
                  : 'Test connection'}
              </Button>
              {twilio.connectionTest.message ? (
                <p
                  className="text-muted-foreground mt-1 text-xs"
                  data-testid="twilio-preflight-result"
                  role="status"
                >
                  {twilio.connectionTest.message}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </Card>
    </TooltipProvider>
  );
}
